import React, { useState, useEffect, useCallback } from "react";
import { Modal, Spinner, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";

const ChannelManager = () => {
  const [planInfo, setPlanInfo]         = useState(null);
  const [channels, setChannels]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [sessionStatus, setSessionStatus] = useState("checking"); // checking | not_connected | pending_qr | connected
  const [sessionPhone, setSessionPhone] = useState("");
  const [useMainDevice, setUseMainDevice] = useState(false);
  const [syncing, setSyncing]           = useState(false);

  // QR modal state
  const [showQRModal, setShowQRModal]   = useState(false);
  const [qrImage, setQrImage]           = useState("");
  const [qrLoading, setQrLoading]       = useState(false);
  const [qrPolling, setQrPolling]       = useState(null);

  // Add channel modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelLink, setNewChannelLink] = useState("");
  const [saving, setSaving]             = useState(false);

  // ── Load plan info + session + channels on mount ────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [planRes, sessionRes, channelsRes] = await Promise.all([
        axiosInstance.get("channels/plan-info"),
        axiosInstance.get("channels/session-status"),
        axiosInstance.get("channels"),
      ]);
      setPlanInfo(planRes.data);
      setSessionStatus(sessionRes.data.status || "not_connected");
      setSessionPhone(sessionRes.data.phone || "");
      setUseMainDevice(!!sessionRes.data.useMainDevice);
      if (channelsRes.data.status) setChannels(channelsRes.data.data || []);
    } catch (err) {
      console.error("ChannelManager loadAll error:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Clean up polling on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => { if (qrPolling) clearInterval(qrPolling); };
  }, [qrPolling]);

  // ── Start QR scan flow ──────────────────────────────────────────────────
  const startQR = async () => {
    setQrLoading(true);
    setQrImage("");
    setShowQRModal(true);
    try {
      const { data } = await axiosInstance.post("channels/session/start-qr");
      if (!data.status) {
        swal("Error", data.message || "Failed to start QR", "error");
        setShowQRModal(false);
        setQrLoading(false);
        return;
      }
      // Browser is launching in the background — poll for QR then for scan
      setSessionStatus("pending_qr");

      const interval = setInterval(async () => {
        try {
          const { data: statusData } = await axiosInstance.get("channels/session/qr-status");
          if (statusData.qrStatus === "qr_ready" && statusData.qr) {
            // QR is ready — display it
            setQrImage(statusData.qr);
            setQrLoading(false);
          } else if (statusData.qrStatus === "connected") {
            clearInterval(interval);
            setQrPolling(null);
            setShowQRModal(false);
            setSessionStatus("connected");
            setSessionPhone(statusData.phone || "");
            swal("Connected!", "WhatsApp session connected successfully.", "success");
            loadAll();
          } else if (statusData.qrStatus === "error") {
            clearInterval(interval);
            setQrPolling(null);
            swal("Error", statusData.error || "Failed to generate QR code. Please try again.", "error");
            setShowQRModal(false);
            setQrLoading(false);
          }
        } catch {}
      }, 3000);
      setQrPolling(interval);
    } catch (err) {
      swal("Error", err.message || "Server error", "error");
      setShowQRModal(false);
      setQrLoading(false);
    }
  };

  // ── Sync channels from the connected WhatsApp ───────────────────────────
  const syncChannels = async () => {
    setSyncing(true);
    try {
      const { data } = await axiosInstance.post("channels/sync");
      swal(data.status ? "Synced" : "Sync", data.message, data.status ? "success" : "info");
      if (data.status) loadAll();
    } catch (err) {
      swal("Error", err?.response?.data?.message || err.message, "error");
    }
    setSyncing(false);
  };

  // ── Cancel QR flow ──────────────────────────────────────────────────────
  const cancelQR = async () => {
    if (qrPolling) { clearInterval(qrPolling); setQrPolling(null); }
    setShowQRModal(false);
    setQrImage("");
    try { await axiosInstance.post("channels/session/cancel-qr"); } catch {}
    setSessionStatus("not_connected");
  };

  // ── Disconnect session ──────────────────────────────────────────────────
  const disconnect = async () => {
    const confirm = await swal({
      title: "Disconnect WhatsApp?",
      text: "You will need to scan the QR code again to reconnect.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirm) return;
    try {
      await axiosInstance.post("channels/session/disconnect");
      setSessionStatus("not_connected");
      setSessionPhone("");
      swal("Disconnected", "WhatsApp session removed.", "success");
    } catch (err) {
      swal("Error", err.message, "error");
    }
  };

  // ── Add channel ─────────────────────────────────────────────────────────
  const addChannel = async (e) => {
    e.preventDefault();
    if (!newChannelName.trim()) return;
    setSaving(true);
    try {
      const { data } = await axiosInstance.post("channels", { name: newChannelName.trim(), channelLink: newChannelLink.trim() });
      if (data.status) {
        swal("Added!", "Channel added successfully.", "success");
        setShowAddModal(false);
        setNewChannelName("");
        setNewChannelLink("");
        loadAll();
      } else {
        swal("Error", data.message, "error");
      }
    } catch (err) {
      swal("Error", err?.response?.data?.message || err.message, "error");
    }
    setSaving(false);
  };

  // ── Remove channel ──────────────────────────────────────────────────────
  const removeChannel = async (ch) => {
    const confirm = await swal({
      title: `Remove "${ch.name}"?`,
      text: "Pending posts for this channel will be cancelled.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirm) return;
    try {
      const { data } = await axiosInstance.delete(`channels/${ch._id}`);
      if (data.status) {
        swal("Removed", "Channel removed.", "success");
        loadAll();
      } else {
        swal("Error", data.message, "error");
      }
    } catch (err) {
      swal("Error", err.message, "error");
    }
  };

  // ── Not on channel scheduler plan ───────────────────────────────────────
  if (!loading && planInfo && !planInfo.enabled) {
    return (
      <>
        <PageTitle activeMenu="Channel Scheduler" motherMenu="Dashboard" />
        <div className="row">
          <div className="col-12">
            <div className="card text-center p-5">
              <h4>Channel Scheduler Not Available</h4>
              <p className="text-muted mt-2">
                Your current plan does not include the Channel Scheduler add-on.
              </p>
              <Link to="/packs" className="btn btn-primary mt-3" style={{ maxWidth: 200, margin: "0 auto" }}>
                Upgrade Plan
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Channel Scheduler" motherMenu="Dashboard" />

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          {/* ── Session Status Card ────────────────────────────────────── */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="card">
                <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <h5 className="mb-1">WhatsApp Connection</h5>
                    {sessionStatus === "connected" && (
                      <span>
                        <Badge bg="success">Connected</Badge>
                        {sessionPhone && <span className="ms-2 text-muted small">{sessionPhone}</span>}
                      </span>
                    )}
                    {sessionStatus === "not_connected" && (
                      <Badge bg="danger">Not Connected</Badge>
                    )}
                    {sessionStatus === "pending_qr" && (
                      <Badge bg="warning" text="dark">Waiting for QR Scan</Badge>
                    )}
                    {sessionStatus === "checking" && (
                      <Badge bg="secondary">Checking…</Badge>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    {sessionStatus !== "connected" && useMainDevice && (
                      <Link to="/device" className="btn btn-primary btn-sm">
                        📱 Connect a device
                      </Link>
                    )}
                    {sessionStatus !== "connected" && !useMainDevice && (
                      <button className="btn btn-primary btn-sm" onClick={startQR}>
                        📱 Connect WhatsApp
                      </button>
                    )}
                    {useMainDevice && sessionStatus === "connected" && (
                      <button className="btn btn-outline-primary btn-sm" onClick={syncChannels} disabled={syncing}>
                        {syncing ? <Spinner animation="border" size="sm" /> : "🔄 Sync Channels"}
                      </button>
                    )}
                    {sessionStatus === "connected" && !useMainDevice && (
                      <button className="btn btn-outline-danger btn-sm" onClick={disconnect}>
                        Disconnect
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Plan Usage ────────────────────────────────────────────── */}
          {planInfo && (
            <div className="row mb-3">
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="mb-0" style={{ color: "#17556D" }}>
                      {planInfo.channelsUsed ?? 0}
                      <span className="text-muted fs-5">/{planInfo.maxChannels ?? 0}</span>
                    </h3>
                    <p className="text-muted mb-0 small">Channels Used</p>
                    <div className="progress mt-2" style={{ height: 6 }}>
                      <div
                        className="progress-bar"
                        style={{
                          width: planInfo.maxChannels > 0
                            ? `${Math.min(100, (planInfo.channelsUsed / planInfo.maxChannels) * 100)}%`
                            : "0%",
                          background: "#17556D",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="mb-0" style={{ color: "#17556D" }}>
                      {planInfo.maxPostsPerChannelPerDay > 0 ? planInfo.maxPostsPerChannelPerDay : "∞"}
                    </h3>
                    <p className="text-muted mb-0 small">Posts / Channel / Day</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body text-center">
                    <h3 className="mb-0" style={{ color: "#17556D" }}>
                      {planInfo.maxScheduleDaysAhead ?? 7}
                    </h3>
                    <p className="text-muted mb-0 small">Days Scheduling Ahead</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Channels List ──────────────────────────────────────────── */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">My Channels</h4>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAddModal(true)}
                    disabled={planInfo && planInfo.channelsUsed >= planInfo.maxChannels}
                  >
                    + Add Channel
                  </button>
                </div>
                <div className="card-body">
                  {channels.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <p>No channels added yet.</p>
                      <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
                        Add Your First Channel
                      </button>
                    </div>
                  ) : (
                    <div className="row">
                      {channels.map(ch => (
                        <div className="col-md-6 col-lg-4 mb-3" key={ch._id}>
                          <div className="card border mb-0" style={{ borderRadius: 8 }}>
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="mb-1 fw-bold">📢 {ch.name}</h6>
                                  <div className="small text-muted mt-1">
                                    <span>Today: <strong>{ch.postsToday ?? 0}</strong>
                                      {planInfo?.maxPostsPerChannelPerDay > 0
                                        ? `/${planInfo.maxPostsPerChannelPerDay}`
                                        : ""}
                                    </span>
                                    <span className="ms-3">Month: <strong>{ch.postsThisMonth ?? 0}</strong>
                                      {planInfo?.maxPostsPerChannelPerMonth > 0
                                        ? `/${planInfo.maxPostsPerChannelPerMonth}`
                                        : ""}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  className="btn btn-outline-danger btn-xs"
                                  onClick={() => removeChannel(ch)}
                                  title="Remove channel"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="mt-2 d-flex gap-2">
                                <Link
                                  to={`/channel-schedule?channelId=${ch._id}&channelName=${encodeURIComponent(ch.name)}`}
                                  className="btn btn-primary btn-xs flex-fill text-center"
                                >
                                  + Schedule Post
                                </Link>
                                <Link
                                  to={`/channel-posts?channelId=${ch._id}&channelName=${encodeURIComponent(ch.name)}`}
                                  className="btn btn-outline-secondary btn-xs flex-fill text-center"
                                >
                                  View Posts
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── QR Modal ────────────────────────────────────────────────────── */}
      <Modal show={showQRModal} onHide={cancelQR} centered backdrop="static">
        <Modal.Header>
          <Modal.Title>Scan QR Code</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          {qrLoading ? (
            <>
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Opening WhatsApp Web…</p>
            </>
          ) : qrImage ? (
            <>
              <img src={qrImage} alt="QR Code" style={{ width: 220, height: 220 }} />
              <p className="mt-3 text-muted small">
                Open WhatsApp on your phone → <strong>Linked Devices</strong> → <strong>Link a Device</strong> → Scan this code
              </p>
              <div className="d-flex align-items-center justify-content-center gap-2 mt-2">
                <Spinner animation="border" size="sm" variant="success" />
                <span className="text-muted small">Waiting for scan…</span>
              </div>
            </>
          ) : (
            <p className="text-muted">Generating QR code…</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={cancelQR}>Cancel</button>
        </Modal.Footer>
      </Modal>

      {/* ── Add Channel Modal ────────────────────────────────────────────── */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header>
          <Modal.Title>Add Channel</Modal.Title>
          <button className="close" onClick={() => setShowAddModal(false)}><span>×</span></button>
        </Modal.Header>
        <form onSubmit={addChannel}>
          <Modal.Body>
            <div className="form-group">
              <label className="text-black font-w500">Channel Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Channel name"
                value={newChannelName}
                onChange={e => setNewChannelName(e.target.value)}
                required={!useMainDevice}
                autoFocus
              />
              <small className="text-muted">
                A label for this channel (shown in your list).
              </small>
            </div>
            <div className="form-group mt-3">
              <label className="text-black font-w500">Channel Link</label>
              <input
                type="text"
                className="form-control"
                placeholder="https://whatsapp.com/channel/XXXXXXXX"
                value={newChannelLink}
                onChange={e => setNewChannelLink(e.target.value)}
                required
              />
              <small className="text-muted">
                Share link of a channel you own or admin. WhatsApp must be connected first.
              </small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Add Channel"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default ChannelManager;
